import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Plus, X, Save, Check, Ban, FileText } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { fmtMoney } from "@/utils/money";
import { TrashDeleteAction } from "@/components/trash/TrashDeleteAction";

type Inv = {
    id: string;
    invoice_number: string;
    person_id: string;
    due_date: string;
    currency: string;
    total_minor: number;
    effective_total_minor: number;
    effective_paid_minor: number;
    debt_minor: number;
    status: "draft" | "confirmed" | "void";
    period_start: string | null;
    period_end: string | null;
};

type Person = { id: string; first_name: string; last_name: string };
type Plan = { id: string; name: string; amount_minor: number; currency: string };

export default function Invoices() {
    const { t } = useTranslation();
    const { hasPerm } = useAuth();
    const canCreate = hasPerm("finance.create");
    const canConfirm = hasPerm("finance.confirm");
    const canVoid = hasPerm("finance.void");
    const [items, setItems] = useState<Inv[]>([]);
    const [status, setStatus] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [showNew, setShowNew] = useState(false);
    const [people, setPeople] = useState<Person[]>([]);
    const [plans, setPlans] = useState<Plan[]>([]);
    const [personMap, setPersonMap] = useState<Record<string, Person>>({});

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const [inv, pe, pp] = await Promise.all([
                api.get(`/invoices?limit=200${status ? `&status=${status}` : ""}`),
                api.get("/people?limit=200"),
                api.get("/pricing-plans"),
            ]);
            setItems(inv.data.items);
            setPeople(pe.data.items);
            const m: Record<string, Person> = {};
            pe.data.items.forEach((p: Person) => { m[p.id] = p; });
            setPersonMap(m);
            setPlans(pp.data.items);
        } catch (e: any) { setErr(formatApiError(e)); } finally { setLoading(false); }
    }, [status]);
    useEffect(() => { load(); }, [load]);

    const doConfirm = async (id: string) => {
        try { await api.post(`/invoices/${id}/confirm`); load(); } catch (e: any) { setErr(formatApiError(e)); }
    };
    const doVoid = async (id: string) => {
        const reason = prompt(t("finance.invoice.void_reason_prompt") as string);
        if (!reason) return;
        try { await api.post(`/invoices/${id}/void`, { reason }); load(); } catch (e: any) { setErr(formatApiError(e)); }
    };

    return (
        <div className="space-y-6" data-testid="invoices-page">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <h1 className="text-3xl font-semibold" style={{ color: "var(--text)" }} data-testid="page-title">{t("finance.invoices.title")}</h1>
                {canCreate && <button data-testid="new-invoice-btn" onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}><Plus size={16}/> {t("common.new")}</button>}
            </div>
            <div className="flex gap-2 flex-wrap">
                {["", "draft", "confirmed", "void"].map((s) => (
                    <button key={s} data-testid={`filter-${s || "all"}`} onClick={() => setStatus(s)} className="rounded-full px-3 py-1 text-xs border" style={{ background: status === s ? "var(--selected-bg)" : "var(--input)", color: status === s ? "var(--text)" : "var(--text-muted)", borderColor: status === s ? "var(--primary)" : "var(--border)" }}>
                        {s ? t(`finance.invoice.status.${s}` as any) : t("common.all", { defaultValue: "Hamısı" })}
                    </button>
                ))}
            </div>
            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="invoices-error">{err}</div>}
            <div className="surface-card overflow-hidden">
                {loading ? <div className="p-8 flex justify-center"><Loader2 className="animate-spin"/></div> : items.length === 0 ? (
                    <div className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }} data-testid="invoices-empty">{t("common.empty")}</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead><tr className="text-left" style={{ background: "var(--input)", color: "var(--text-muted)" }}>
                            <th className="px-4 py-2 font-medium">№</th>
                            <th className="px-4 py-2 font-medium">{t("common.name")}</th>
                            <th className="px-4 py-2 font-medium">{t("finance.due_date")}</th>
                            <th className="px-4 py-2 font-medium text-right">{t("finance.total")}</th>
                            <th className="px-4 py-2 font-medium text-right">{t("finance.paid")}</th>
                            <th className="px-4 py-2 font-medium text-right">{t("finance.debt")}</th>
                            <th className="px-4 py-2 font-medium">{t("common.status")}</th>
                            <th className="px-4 py-2 font-medium text-right">{t("common.actions")}</th>
                        </tr></thead>
                        <tbody>
                            {items.map((inv) => {
                                const person = personMap[inv.person_id];
                                return (
                                    <tr key={inv.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`invoice-row-${inv.id}`}>
                                        <td className="px-4 py-3 font-mono text-xs" style={{ color: "var(--text)" }}>{inv.invoice_number}</td>
                                        <td className="px-4 py-3">
                                            {person && <Link to={`/people/${inv.person_id}`} className="hover:underline" style={{ color: "var(--text)" }}>{person.first_name} {person.last_name}</Link>}
                                        </td>
                                        <td className="px-4 py-3" style={{ color: "var(--text-muted)" }}>{inv.due_date}</td>
                                        <td className="px-4 py-3 text-right tabular-nums" style={{ color: "var(--text)" }}>{fmtMoney(inv.effective_total_minor, inv.currency)}</td>
                                        <td className="px-4 py-3 text-right tabular-nums" style={{ color: "var(--text-muted)" }}>{fmtMoney(inv.effective_paid_minor, inv.currency)}</td>
                                        <td className="px-4 py-3 text-right tabular-nums font-medium" style={{ color: inv.debt_minor > 0 ? "var(--error-fg)" : "var(--success-fg)" }}>{fmtMoney(inv.debt_minor, inv.currency)}</td>
                                        <td className="px-4 py-3">
                                            <span className={`status-pill ${inv.status === "confirmed" ? "status-success" : inv.status === "draft" ? "status-warning" : "status-neutral"}`}>
                                                <FileText size={10} className="inline mr-1"/>{t(`finance.invoice.status.${inv.status}` as any)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right space-x-1">
                                            {canConfirm && inv.status === "draft" && (
                                                <button data-testid={`confirm-${inv.id}`} onClick={() => doConfirm(inv.id)} className="text-xs inline-flex items-center gap-1 rounded-md px-2 py-1 border" style={{ borderColor: "var(--border)", color: "var(--success-fg)" }}><Check size={12}/> {t("finance.invoice.confirm")}</button>
                                            )}
                                            {canVoid && inv.status === "confirmed" && (
                                                <button data-testid={`void-${inv.id}`} onClick={() => doVoid(inv.id)} className="text-xs inline-flex items-center gap-1 rounded-md px-2 py-1 border" style={{ borderColor: "var(--border)", color: "var(--error-fg)" }}><Ban size={12}/> {t("finance.invoice.void")}</button>
                                            )}
                                            {/* Phase TRASH-2: soft-delete → trash. Only Institution/Platform owners see this;
                                                TrashDeleteAction hides itself when the caller lacks trash.manage. */}
                                            <TrashDeleteAction
                                                entityType="invoice"
                                                entityId={inv.id}
                                                entityLabel={inv.invoice_number}
                                                entityStatus={inv.status}
                                                variant="icon"
                                                dataTestidPrefix={`invoice-trash-${inv.id}`}
                                                onArchived={load}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
            {showNew && <InvoiceDialog people={people} plans={plans} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
        </div>
    );
}

function InvoiceDialog({ people, plans, onClose, onSaved }: { people: Person[]; plans: Plan[]; onClose: () => void; onSaved: () => void }) {
    const { t } = useTranslation();
    const today = new Date();
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const [state, setState] = useState({
        person_id: "" as string,
        due_date: endOfMonth.toISOString().slice(0, 10),
        currency: "AZN",
        items: [] as { kind: "charge" | "discount"; description: string; amount_minor: number; pricing_plan_id?: string | null }[],
    });
    const [newDesc, setNewDesc] = useState("");
    const [newAmt, setNewAmt] = useState<number>(0);
    const [newKind, setNewKind] = useState<"charge" | "discount">("charge");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const addPlan = (plan: Plan) => {
        setState((s) => ({ ...s, items: [...s.items, { kind: "charge", description: plan.name, amount_minor: plan.amount_minor, pricing_plan_id: plan.id }] }));
    };
    const addManual = () => {
        if (!newDesc || newAmt <= 0) return;
        setState((s) => ({ ...s, items: [...s.items, { kind: newKind, description: newDesc, amount_minor: Math.round(newAmt * 100) }] }));
        setNewDesc(""); setNewAmt(0);
    };
    const removeItem = (i: number) => setState((s) => ({ ...s, items: s.items.filter((_, k) => k !== i) }));

    const submit = async () => {
        setSaving(true); setErr(null);
        try {
            await api.post("/invoices", { person_id: state.person_id, due_date: state.due_date, currency: state.currency, items: state.items });
            onSaved();
        } catch (e: any) { setErr(formatApiError(e)); } finally { setSaving(false); }
    };

    const subtotal = state.items.filter(i => i.kind === "charge").reduce((s, i) => s + i.amount_minor, 0);
    const discount = state.items.filter(i => i.kind === "discount").reduce((s, i) => s + i.amount_minor, 0);
    const total = Math.max(subtotal - discount, 0);

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose} data-testid="invoice-dialog">
            <div className="surface-card w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "var(--border)" }}>
                    <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{t("finance.invoice.new")}</h2>
                    <button onClick={onClose}><X size={18} style={{ color: "var(--text-muted)" }}/></button>
                </div>
                <div className="p-5 overflow-y-auto space-y-4">
                    <label className="block">
                        <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("finance.invoice.person")}</div>
                        <select data-testid="inv-person" value={state.person_id} onChange={(e) => setState({ ...state, person_id: e.target.value })} className="surface-input w-full px-3 py-2 text-sm" style={{ minHeight: 40 }}>
                            <option value="">—</option>
                            {people.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("finance.due_date")}</div>
                        <input data-testid="inv-due" type="date" value={state.due_date} onChange={(e) => setState({ ...state, due_date: e.target.value })} className="surface-input w-full px-3 py-2 text-sm" style={{ minHeight: 40 }} />
                    </label>
                    <div>
                        <div className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>{t("finance.invoice.add_from_plan")}</div>
                        <div className="flex flex-wrap gap-2">
                            {plans.map((p) => <button key={p.id} data-testid={`plan-${p.id}`} type="button" onClick={() => addPlan(p)} className="text-xs rounded-full px-3 py-1 border" style={{ background: "var(--input)", color: "var(--text)", borderColor: "var(--border)" }}>+ {p.name} ({(p.amount_minor / 100).toFixed(2)} {p.currency})</button>)}
                        </div>
                    </div>
                    <div className="border-t pt-3" style={{ borderColor: "var(--border)" }}>
                        <div className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>{t("finance.invoice.items")}</div>
                        {state.items.length === 0 && <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div>}
                        <ul className="space-y-1 mb-3">
                            {state.items.map((it, i) => (
                                <li key={i} className="flex items-center gap-2 text-sm p-2 rounded-md" style={{ background: "var(--input)" }} data-testid={`item-${i}`}>
                                    <span className={`status-pill ${it.kind === "charge" ? "status-info" : "status-warning"}`}>{t(`finance.invoice.item.${it.kind}` as any)}</span>
                                    <span className="flex-1 truncate" style={{ color: "var(--text)" }}>{it.description}</span>
                                    <span className="tabular-nums" style={{ color: "var(--text)" }}>{(it.amount_minor / 100).toFixed(2)}</span>
                                    <button onClick={() => removeItem(i)}><X size={12} style={{ color: "var(--text-muted)" }}/></button>
                                </li>
                            ))}
                        </ul>
                        <div className="grid grid-cols-4 gap-2">
                            <select value={newKind} onChange={(e) => setNewKind(e.target.value as any)} className="surface-input px-2 text-xs" style={{ minHeight: 36 }}>
                                <option value="charge">{t("finance.invoice.item.charge")}</option>
                                <option value="discount">{t("finance.invoice.item.discount")}</option>
                            </select>
                            <input data-testid="new-item-desc" placeholder={t("finance.invoice.item_desc") as string} value={newDesc} onChange={(e) => setNewDesc(e.target.value)} className="col-span-2 surface-input px-2 text-xs" style={{ minHeight: 36 }} />
                            <input data-testid="new-item-amt" type="number" step="0.01" placeholder="0.00" value={newAmt || ""} onChange={(e) => setNewAmt(parseFloat(e.target.value) || 0)} className="surface-input px-2 text-xs" style={{ minHeight: 36 }} />
                        </div>
                        <button data-testid="add-item-btn" type="button" onClick={addManual} className="mt-2 text-xs inline-flex items-center gap-1" style={{ color: "var(--accent)" }}><Plus size={12}/> {t("finance.invoice.add_item")}</button>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t" style={{ borderColor: "var(--border)" }}>
                        <span className="text-sm" style={{ color: "var(--text-muted)" }}>{t("finance.total")}</span>
                        <span className="text-lg font-semibold tabular-nums" style={{ color: "var(--text)" }} data-testid="inv-total">{(total / 100).toFixed(2)} AZN</span>
                    </div>
                    {err && <div className="text-sm" style={{ color: "var(--error-fg)" }} data-testid="inv-error">{err}</div>}
                </div>
                <div className="p-5 border-t" style={{ borderColor: "var(--border)" }}>
                    <button data-testid="inv-save" disabled={saving || !state.person_id || state.items.length === 0} onClick={submit} className="w-full inline-flex justify-center items-center gap-2 rounded-md py-2 text-sm font-medium disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>
                        {saving ? <Loader2 className="animate-spin" size={16}/> : <Save size={16}/>} {t("finance.invoice.save_draft")}
                    </button>
                </div>
            </div>
        </div>
    );
}
